import type { Project } from '@shared/types'

/** Skeletons older than this are treated as failed, not in-flight. */
export const MAX_GENERATION_AGE_MS = 45 * 60 * 1000

/**
 * Skeleton projects whose jobs aren't tracked in context yet (orphaned).
 * `nowMs` is injected so render code can pass a ticking clock and effects
 * can pass a fresh `Date.now()` before acting on the result.
 */
export function selectOrphanedSkeletons(
  projects: Project[],
  activeJobs: ReadonlyArray<{ projectId: string }>,
  currentUserEmail: string,
  nowMs: number,
): Project[] {
  return projects.filter(
    (p) =>
      !p.isDemo &&
      p.data == null &&
      !p.subfunctionCount &&
      !p.automationBuildCount &&
      // A saved zero is a computed FTE summary; only a missing value is uncomputed.
      p.totalFteCount == null &&
      (!p.createdBy || p.createdBy === currentUserEmail) &&
      !activeJobs.some((j) => j.projectId === p.id) &&
      nowMs - new Date(p.createdAt).getTime() < MAX_GENERATION_AGE_MS
  )
}

/** Sidebar/Home sort: by display name, or by last-updated (falling back to created). */
export function compareProjects(
  a: Project,
  b: Project,
  sortBy: 'name' | 'date',
  sortDir: 'asc' | 'desc',
): number {
  const result = sortBy === 'name'
    ? (a.name || a.client || '').localeCompare(b.name || b.client || '')
    : new Date(a.updatedAt || a.createdAt).getTime() - new Date(b.updatedAt || b.createdAt).getTime()
  return sortDir === 'asc' ? result : -result
}
